import * as React from "react";
import {
  Box,
  Button,
  List,
  ListItem,
  Typography,
  useTheme,
  IconButton,
  Divider,
  useMediaQuery,
} from "@mui/material";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { LoadingButton } from "@mui/lab";
import { Stack } from "@mui/material";
import BaseModal from "./global/BaseModal";
import { CardPreferences } from "../services/cardPreferences.service";

// Project Edit Card IDs
export const PROJECT_EDIT_CARD_IDS = {
  bulkUpload: "bulkUpload",
  template: "template",
  workInformation: "workInformation",
  workSite: "workSite", // Sub-card of Work Information
  startDateTime: "startDateTime", // Sub-card of Work Information
  sow: "sow", // Sub-card of Work Information
  pricingDetails: "pricingDetails",
  skills: "skills",
  certifications: "certifications",
  manager: "manager",
  tasks: "tasks",
  additionalInformation: "additionalInformation",
} as const;

export const PROJECT_EDIT_CARD_LABELS: Record<string, string> = {
  [PROJECT_EDIT_CARD_IDS.bulkUpload]: "Bulk Upload",
  [PROJECT_EDIT_CARD_IDS.template]: "Template",
  [PROJECT_EDIT_CARD_IDS.workInformation]: "Work Information",
  [PROJECT_EDIT_CARD_IDS.workSite]: "Where is the Work Site?",
  [PROJECT_EDIT_CARD_IDS.startDateTime]: "Date and Time",
  [PROJECT_EDIT_CARD_IDS.sow]: "Enter SOW",
  [PROJECT_EDIT_CARD_IDS.pricingDetails]: "Pricing Details",
  [PROJECT_EDIT_CARD_IDS.skills]: "Select/Search Skills",
  [PROJECT_EDIT_CARD_IDS.certifications]: "Required Certifications",
  [PROJECT_EDIT_CARD_IDS.manager]: "Assign Work Manager",
  [PROJECT_EDIT_CARD_IDS.tasks]: "Tasks",
  [PROJECT_EDIT_CARD_IDS.additionalInformation]: "Additional Information",
};

export const DEFAULT_PROJECT_EDIT_CARD_ORDER = Object.values(PROJECT_EDIT_CARD_IDS);

// Work Information Sub-Card IDs
export const WORK_INFORMATION_SUB_CARD_IDS = {
  workSite: PROJECT_EDIT_CARD_IDS.workSite,
  startDateTime: PROJECT_EDIT_CARD_IDS.startDateTime,
  sow: PROJECT_EDIT_CARD_IDS.sow,
} as const;

export const WORK_INFORMATION_SUB_CARD_LABELS: Record<string, string> = {
  [WORK_INFORMATION_SUB_CARD_IDS.workSite]: "Where is the Work Site?",
  [WORK_INFORMATION_SUB_CARD_IDS.startDateTime]: "Date and Time",
  [WORK_INFORMATION_SUB_CARD_IDS.sow]: "Enter SOW",
};

export const DEFAULT_WORK_INFORMATION_SUB_CARD_ORDER = [
  WORK_INFORMATION_SUB_CARD_IDS.workSite,
  WORK_INFORMATION_SUB_CARD_IDS.startDateTime,
  WORK_INFORMATION_SUB_CARD_IDS.sow,
];

// Required cards that cannot be hidden (only reorderable)
export const REQUIRED_CARD_IDS = [
  PROJECT_EDIT_CARD_IDS.workInformation,
  PROJECT_EDIT_CARD_IDS.pricingDetails,
  PROJECT_EDIT_CARD_IDS.skills,
  PROJECT_EDIT_CARD_IDS.manager,
] as const;

// Required sub-cards that cannot be hidden
export const REQUIRED_WORK_INFORMATION_SUB_CARD_IDS = [
  WORK_INFORMATION_SUB_CARD_IDS.workSite,
  WORK_INFORMATION_SUB_CARD_IDS.startDateTime,
  WORK_INFORMATION_SUB_CARD_IDS.sow,
] as const;

interface SortableItemProps {
  id: string;
  label: string;
  isHidden: boolean;
  onToggleVisibility: (id: string) => void;
  showDetailButton?: boolean;
  onDetailClick?: (id: string) => void;
  isDetailExpanded?: boolean;
  isRequired?: boolean;
}

const SortableItem: React.FC<SortableItemProps> = ({
  id,
  label,
  isHidden,
  onToggleVisibility,
  showDetailButton = false,
  onDetailClick,
  isDetailExpanded = false,
  isRequired = false,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md"));
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <ListItem
      ref={setNodeRef}
      style={style}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: { xs: 1, sm: 1.5, md: 2 },
        py: { xs: 1, sm: 1.25, md: 1.5 },
        px: { xs: 1, sm: 1.5, md: 2 },
        mb: { xs: 0.75, sm: 1 },
        borderRadius: { xs: 1.5, sm: 2 },
        "@media (orientation: landscape) and (max-width: 900px)": {
          gap: { xs: 0.75, sm: 1 },
          py: { xs: 0.75, sm: 1 },
          px: { xs: 0.75, sm: 1.25 },
          mb: { xs: 0.5, sm: 0.75 },
          borderRadius: { xs: 1, sm: 1.5 },
        },
        backgroundColor: isDragging
          ? theme.palette.mode === "dark"
            ? "rgba(255, 255, 255, 0.1)"
            : "rgba(0, 0, 0, 0.05)"
          : theme.palette.mode === "dark"
            ? "rgba(255, 255, 255, 0.05)"
            : "rgba(0, 0, 0, 0.02)",
        border: `1px solid ${theme.palette.mode === "dark"
          ? "rgba(255, 255, 255, 0.1)"
          : "rgba(0, 0, 0, 0.08)"
          }`,
        transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
        "&:hover": {
          backgroundColor:
            theme.palette.mode === "dark"
              ? "rgba(255, 255, 255, 0.08)"
              : "rgba(0, 0, 0, 0.04)",
          borderColor: theme.palette.primary.main + "40",
        },
      }}
    >
      <IconButton
        {...attributes}
        {...listeners}
        size={isMobile ? "small" : "medium"}
        sx={{
          cursor: "grab",
          color: "text.secondary",
          "&:active": {
            cursor: "grabbing",
          },
          "&:hover": {
            color: "primary.main",
            backgroundColor: "primary.light" + "20",
          },
        }}
      >
        <DragIndicatorIcon fontSize={isMobile ? "small" : "medium"} />
      </IconButton>
      <Typography
        sx={{
          flex: 1,
          fontWeight: 500,
          opacity: isHidden ? 0.5 : 1,
          textDecoration: isHidden ? "line-through" : "none",
          fontSize: { xs: "0.875rem", sm: "0.9375rem", md: "1rem" },
          "@media (orientation: landscape) and (max-width: 900px)": {
            fontSize: { xs: "0.8125rem", sm: "0.875rem" },
          },
        }}
      >
        {label}
      </Typography>
      {showDetailButton && onDetailClick && (
        <IconButton
          onClick={(e) => {
            e.stopPropagation();
            onDetailClick(id);
          }}
          size={isMobile ? "small" : "medium"}
          sx={{ mr: { xs: 0.25, sm: 0.5 } }}
        >
          {isDetailExpanded ? (
            <ExpandLessIcon fontSize={isMobile ? "small" : "medium"} />
          ) : (
            <ExpandMoreIcon fontSize={isMobile ? "small" : "medium"} />
          )}
        </IconButton>
      )}
      {!isRequired && (
        <IconButton
          onClick={() => onToggleVisibility(id)}
          size={isMobile ? "small" : "medium"}
          sx={{
            color: isHidden ? "text.secondary" : "primary.main",
            "&:hover": {
              backgroundColor: "primary.light" + "20",
            },
          }}
        >
          {isHidden ? (
            <VisibilityOffIcon fontSize={isMobile ? "small" : "medium"} />
          ) : (
            <VisibilityIcon fontSize={isMobile ? "small" : "medium"} />
          )}
        </IconButton>
      )}
      {isRequired && (
        <Typography
          variant="caption"
          sx={{
            color: "text.secondary",
            fontSize: { xs: "0.7rem", sm: "0.75rem" },
            px: { xs: 0.5, sm: 1 },
            fontStyle: "italic",
          }}
        >
          Required
        </Typography>
      )}
    </ListItem>
  );
};

interface ProjectEditCustomizeLayoutModalProps {
  open: boolean;
  onClose: () => void;
  preferences: CardPreferences | undefined;
  onSave: (preferences: {
    cardOrder: string[];
    hiddenCards: string[];
    workInformationSubCardsOrder: string[];
    hiddenWorkInformationSubCards: string[];
  }) => Promise<void>;
}

const ProjectEditCustomizeLayoutModal: React.FC<ProjectEditCustomizeLayoutModalProps> = ({
  open,
  onClose,
  preferences,
  onSave,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md"));
  const [cardOrder, setCardOrder] = React.useState<string[]>([]);
  const [hiddenCards, setHiddenCards] = React.useState<string[]>([]);
  const [workInformationSubCardsOrder, setWorkInformationSubCardsOrder] = React.useState<string[]>(DEFAULT_WORK_INFORMATION_SUB_CARD_ORDER);
  const [hiddenWorkInformationSubCards, setHiddenWorkInformationSubCards] = React.useState<string[]>([]);
  const [isSaving, setIsSaving] = React.useState(false);
  const [expandedWorkInformationDetail, setExpandedWorkInformationDetail] = React.useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Initialize state from preferences
  React.useEffect(() => {
    if (preferences) {
      // If cardOrder is empty array or undefined, use default
      const order = (!preferences.cardOrder || preferences.cardOrder.length === 0)
        ? DEFAULT_PROJECT_EDIT_CARD_ORDER
        : preferences.cardOrder;
      // Filter to only include valid project edit card IDs (excluding sub-cards)
      const filteredOrder = order.filter((id) =>
        Object.values(PROJECT_EDIT_CARD_IDS).includes(id as any) &&
        !Object.values(WORK_INFORMATION_SUB_CARD_IDS).includes(id as any)
      );
      // Add any missing cards from default order (excluding sub-cards)
      const mainCardIds = DEFAULT_PROJECT_EDIT_CARD_ORDER.filter(
        (id) => !Object.values(WORK_INFORMATION_SUB_CARD_IDS).includes(id as any)
      );
      const missingCards = mainCardIds.filter(
        (id) => !filteredOrder.includes(id)
      );
      const finalOrder = [...filteredOrder, ...missingCards];
      setCardOrder(finalOrder);
      // Filter hiddenCards to exclude required cards
      const filteredHidden = (preferences.hiddenCards || []).filter(
        (id) => !REQUIRED_CARD_IDS.includes(id as any)
      );
      setHiddenCards(filteredHidden);

      // Initialize Work Information sub-cards
      const allSubCardIds = Object.values(WORK_INFORMATION_SUB_CARD_IDS);
      const savedSubCardsOrder =
        preferences.details?.workInformation?.workInformationSubCardsOrder ||
        DEFAULT_WORK_INFORMATION_SUB_CARD_ORDER;
      const filteredSubCardsOrder = savedSubCardsOrder.filter(
        (id: string) => allSubCardIds.includes(id as any)
      );
      const missingSubCards = DEFAULT_WORK_INFORMATION_SUB_CARD_ORDER.filter(
        (id: string) => !filteredSubCardsOrder.includes(id)
      );
      const finalSubCardsOrder = [...filteredSubCardsOrder, ...missingSubCards];

      const savedHiddenSubCards =
        preferences.details?.workInformation?.hiddenWorkInformationSubCards || [];
      // Filter hidden sub-cards to exclude required sub-cards
      const filteredHiddenSubCards = savedHiddenSubCards.filter(
        (id: string) =>
          allSubCardIds.includes(id as any) &&
          !REQUIRED_WORK_INFORMATION_SUB_CARD_IDS.includes(id as any)
      );

      setWorkInformationSubCardsOrder(finalSubCardsOrder);
      setHiddenWorkInformationSubCards(filteredHiddenSubCards);
    } else {
      setCardOrder(DEFAULT_PROJECT_EDIT_CARD_ORDER.filter(
        (id) => !Object.values(WORK_INFORMATION_SUB_CARD_IDS).includes(id as any)
      ));
      setHiddenCards([]);
      setWorkInformationSubCardsOrder(DEFAULT_WORK_INFORMATION_SUB_CARD_ORDER);
      setHiddenWorkInformationSubCards([]);
    }
  }, [preferences, open]);

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      setCardOrder((items) => {
        const oldIndex = items.indexOf(active.id as string);
        const newIndex = items.indexOf(over.id as string);
        return arrayMove(items, oldIndex, newIndex);
      });
    }
  };

  const handleToggleVisibility = (cardId: string) => {
    // Prevent hiding required cards
    if (REQUIRED_CARD_IDS.includes(cardId as any)) {
      return;
    }
    setHiddenCards((prev) =>
      prev.includes(cardId)
        ? prev.filter((id) => id !== cardId)
        : [...prev, cardId]
    );
  };

  const handleWorkInformationSubCardsDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      setWorkInformationSubCardsOrder((items) => {
        const oldIndex = items.indexOf(active.id as string);
        const newIndex = items.indexOf(over.id as string);
        return arrayMove(items, oldIndex, newIndex);
      });
    }
  };

  const handleToggleWorkInformationSubCardsVisibility = (sectionId: string) => {
    // Prevent hiding required sub-cards
    if (REQUIRED_WORK_INFORMATION_SUB_CARD_IDS.includes(sectionId as any)) {
      return;
    }
    setHiddenWorkInformationSubCards((prev) =>
      prev.includes(sectionId)
        ? prev.filter((id) => id !== sectionId)
        : [...prev, sectionId]
    );
  };

  const handleWorkInformationDetailClick = (cardId: string) => {
    if (cardId === PROJECT_EDIT_CARD_IDS.workInformation) {
      setExpandedWorkInformationDetail((prev) => !prev);
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await onSave({
        cardOrder,
        hiddenCards,
        workInformationSubCardsOrder,
        hiddenWorkInformationSubCards,
      });
      onClose();
    } catch (error) {
      console.error("Failed to save preferences:", error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleReset = () => {
    setCardOrder(DEFAULT_PROJECT_EDIT_CARD_ORDER.filter(
      (id) => !Object.values(WORK_INFORMATION_SUB_CARD_IDS).includes(id as any)
    ));
    setHiddenCards([]);
    setWorkInformationSubCardsOrder(DEFAULT_WORK_INFORMATION_SUB_CARD_ORDER);
    setHiddenWorkInformationSubCards([]);
    setExpandedWorkInformationDetail(false);
  };

  const footer = (
    <Box
      sx={{
        display: "flex",
        flexDirection: { xs: "column", sm: "row" },
        gap: { xs: 1, sm: 1.5 },
        width: "100%",
        justifyContent: { xs: "stretch", sm: "flex-end" },
        "@media (orientation: landscape) and (max-width: 900px)": {
          gap: { xs: 0.75, sm: 1 },
          flexDirection: { xs: "column", sm: "row" },
        },
      }}
    >
      <Button
        onClick={handleReset}
        disabled={isSaving}
        fullWidth={isMobile}
        size={isMobile ? "medium" : "large"}
        sx={{
          fontSize: { xs: "0.875rem", sm: "1rem" },
          order: { xs: 3, sm: 1 },
          mr: { xs: 0, sm: "auto" },
          "@media (orientation: landscape) and (max-width: 900px)": {
            fontSize: { xs: "0.8125rem", sm: "0.875rem" },
            py: { xs: 0.5, sm: 0.75 },
          },
        }}
      >
        Reset to Default
      </Button>
      <Button
        onClick={onClose}
        disabled={isSaving}
        fullWidth={isMobile}
        size={isMobile ? "medium" : "large"}
        sx={{
          fontSize: { xs: "0.875rem", sm: "1rem" },
          order: { xs: 2, sm: 2 },
          "@media (orientation: landscape) and (max-width: 900px)": {
            fontSize: { xs: "0.8125rem", sm: "0.875rem" },
            py: { xs: 0.5, sm: 0.75 },
          },
        }}
      >
        Cancel
      </Button>
      <LoadingButton
        onClick={handleSave}
        variant="contained"
        loading={isSaving}
        fullWidth={isMobile}
        size={isMobile ? "medium" : "large"}
        sx={{
          fontSize: { xs: "0.875rem", sm: "1rem" },
          order: { xs: 1, sm: 3 },
          "@media (orientation: landscape) and (max-width: 900px)": {
            fontSize: { xs: "0.8125rem", sm: "0.875rem" },
            py: { xs: 0.5, sm: 0.75 },
          },
        }}
      >
        Save Changes
      </LoadingButton>
    </Box>
  );

  return (
    <BaseModal
      open={open}
      onClose={onClose}
      title="Customize Layout"
      footer={footer}
      maxWidth={isMobile ? "xs" : isTablet ? "sm" : "md"}
      useGlassCard={false}
    >
      <Box sx={{
        p: { xs: 2, sm: 2.5, md: 3 },
        "@media (orientation: landscape) and (max-width: 900px)": {
          p: { xs: 1.5, sm: 2 },
        },
      }}>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            mb: { xs: 2, sm: 2.5, md: 3 },
            fontSize: { xs: "0.8125rem", sm: "0.875rem", md: "0.9375rem" },
            "@media (orientation: landscape) and (max-width: 900px)": {
              mb: { xs: 1.5, sm: 2 },
              fontSize: { xs: "0.75rem", sm: "0.8125rem" },
            },
          }}
        >
          Drag and drop cards to reorder them. Click the eye icon to show or
          hide cards.
        </Typography>
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={cardOrder}
            strategy={verticalListSortingStrategy}
          >
            <List sx={{ p: 0 }}>
              {cardOrder
                .filter((cardId) => PROJECT_EDIT_CARD_LABELS[cardId]) // Only show valid cards
                .map((cardId, index, filteredArray) => (
                  <React.Fragment key={cardId}>
                    <SortableItem
                      id={cardId}
                      label={PROJECT_EDIT_CARD_LABELS[cardId] || cardId}
                      isHidden={hiddenCards.includes(cardId)}
                      onToggleVisibility={handleToggleVisibility}
                      showDetailButton={cardId === PROJECT_EDIT_CARD_IDS.workInformation}
                      onDetailClick={handleWorkInformationDetailClick}
                      isDetailExpanded={expandedWorkInformationDetail && cardId === PROJECT_EDIT_CARD_IDS.workInformation}
                      isRequired={REQUIRED_CARD_IDS.includes(cardId as any)}
                    />
                    {cardId === PROJECT_EDIT_CARD_IDS.workInformation && expandedWorkInformationDetail && (
                      <Box
                        sx={{
                          pl: { xs: 2, sm: 3, md: 4 },
                          pr: { xs: 1, sm: 1.5, md: 2 },
                          pt: { xs: 1.5, sm: 2 },
                          pb: { xs: 1.5, sm: 2 },
                          "@media (orientation: landscape) and (max-width: 900px)": {
                            pl: { xs: 1.5, sm: 2 },
                            pr: { xs: 0.75, sm: 1 },
                            pt: { xs: 1, sm: 1.5 },
                            pb: { xs: 1, sm: 1.5 },
                          },
                        }}
                      >
                        <Stack
                          spacing={1}
                          sx={{
                            mb: { xs: 1.5, sm: 2 },
                            "@media (orientation: landscape) and (max-width: 900px)": {
                              mb: { xs: 1, sm: 1.5 },
                              "& > * + *": {
                                mt: { xs: 0.75, sm: 1 },
                              },
                            },
                          }}
                        >
                          <Typography
                            variant="subtitle2"
                            sx={{
                              fontWeight: 600,
                              fontSize: { xs: "0.875rem", sm: "0.9375rem", md: "1rem" },
                              "@media (orientation: landscape) and (max-width: 900px)": {
                                fontSize: { xs: "0.8125rem", sm: "0.875rem" },
                              },
                            }}
                          >
                            Work Information Sections
                          </Typography>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{
                              fontSize: { xs: "0.75rem", sm: "0.8rem", md: "0.875rem" },
                              "@media (orientation: landscape) and (max-width: 900px)": {
                                fontSize: { xs: "0.7rem", sm: "0.75rem" },
                              },
                            }}
                          >
                            Customize which sections are shown inside the Work Information card.
                          </Typography>
                        </Stack>

                        <DndContext
                          sensors={sensors}
                          collisionDetection={closestCenter}
                          onDragEnd={handleWorkInformationSubCardsDragEnd}
                        >
                          <SortableContext
                            items={workInformationSubCardsOrder}
                            strategy={verticalListSortingStrategy}
                          >
                            <List sx={{ p: 0 }}>
                              {workInformationSubCardsOrder
                                .filter((sectionId) => WORK_INFORMATION_SUB_CARD_LABELS[sectionId])
                                .map((sectionId, sectionIndex, sectionFilteredArray) => (
                                  <React.Fragment key={sectionId}>
                                    <SortableItem
                                      id={sectionId}
                                      label={WORK_INFORMATION_SUB_CARD_LABELS[sectionId] || sectionId}
                                      isHidden={hiddenWorkInformationSubCards.includes(sectionId)}
                                      onToggleVisibility={handleToggleWorkInformationSubCardsVisibility}
                                      isRequired={REQUIRED_WORK_INFORMATION_SUB_CARD_IDS.includes(sectionId as any)}
                                    />
                                    {sectionIndex < sectionFilteredArray.length - 1 && (
                                      <Divider sx={{
                                        mx: { xs: 1, sm: 1.5, md: 2 },
                                        "@media (orientation: landscape) and (max-width: 900px)": {
                                          mx: { xs: 0.75, sm: 1 },
                                        },
                                      }} />
                                    )}
                                  </React.Fragment>
                                ))}
                            </List>
                          </SortableContext>
                        </DndContext>
                      </Box>
                    )}
                    {index < filteredArray.length - 1 && (
                      <Divider sx={{
                        mx: { xs: 1, sm: 1.5, md: 2 },
                        "@media (orientation: landscape) and (max-width: 900px)": {
                          mx: { xs: 0.75, sm: 1 },
                        },
                      }} />
                    )}
                  </React.Fragment>
                ))}
            </List>
          </SortableContext>
        </DndContext>
      </Box>
    </BaseModal>
  );
};

export default ProjectEditCustomizeLayoutModal;

